import {
  type GkAirProxyServiceWebResponse,
  type GkAirProxyServiceWebResponseRecommendationsValue,
} from '@redteclab/api/clients/crosssellers-proxy-service'

import {
  gtmDataLayerPush,
  UDP_TRACKING_EVENT_RECOMMENDATION_RESPONSE,
} from '../../gtm'
import { type UdpTrackingMetadata } from '../../udp-tracking'

type RecommendationResponseProduct = {
  id: string
  item_reco_score: number | null
  tracking_token: string | null
  widget_model_name: string | null
  widget_model_version: string | null
}

type RecommendationResponseRecommendation = {
  item_count: number
  products: RecommendationResponseProduct[]
  recommendation_id: string
  widget_id: string
}

type RecommendationResponseEvent = {
  ctx_pid: string | null
  event: typeof UDP_TRACKING_EVENT_RECOMMENDATION_RESPONSE
  latency: number
  metadata: UdpTrackingMetadata
  recommendations: RecommendationResponseRecommendation[]
  request_id: string
  service_id: string
  status: number | null
}

const getNullableNumber = (value: unknown): number | null =>
  typeof value === 'number' ? value : null

const getNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null

// Preserve the response-linked ID without changing the generated API response.
const recommendationIds = new WeakMap<
  GkAirProxyServiceWebResponseRecommendationsValue,
  string
>()

export const recommendationResponseGetOrCreateRecommendationId = (
  recommendation: GkAirProxyServiceWebResponseRecommendationsValue,
): string => {
  const existingRecommendationId = recommendationIds.get(recommendation)

  if (existingRecommendationId) {
    return existingRecommendationId
  }

  const recommendationId = `reco_${crypto.randomUUID()}`

  recommendationIds.set(recommendation, recommendationId)

  return recommendationId
}

const recommendationResponseMapRecommendations = (
  response?: GkAirProxyServiceWebResponse,
): RecommendationResponseRecommendation[] =>
  Object.entries(response?.recommendations ?? {}).map(
    ([widgetId, recommendation]) => ({
      item_count: recommendation.content.length,
      products: recommendation.content.map(({ attributes, data }) => ({
        id: data.UID,
        item_reco_score: getNullableNumber(attributes.item_reco_score),
        tracking_token: getNullableString(attributes.trackingtoken),
        widget_model_name: getNullableString(attributes.widget_model_name),
        widget_model_version: getNullableString(
          attributes.widget_model_version,
        ),
      })),
      recommendation_id:
        recommendationResponseGetOrCreateRecommendationId(recommendation),
      widget_id: widgetId,
    }),
  )

export const recommendationResponseDataLayerPush = ({
  contextProductId,
  latency,
  metadata,
  requestId,
  response,
  responseStatusCode,
  serviceId,
}: {
  contextProductId: string
  latency: number
  metadata: UdpTrackingMetadata
  requestId: string
  response?: GkAirProxyServiceWebResponse
  responseStatusCode: number | null
  serviceId: string
}): void => {
  const event: RecommendationResponseEvent = {
    ctx_pid: contextProductId === '' ? null : contextProductId,
    event: UDP_TRACKING_EVENT_RECOMMENDATION_RESPONSE,
    latency,
    metadata,
    recommendations: recommendationResponseMapRecommendations(response),
    request_id: requestId,
    service_id: serviceId,
    status: responseStatusCode,
  }

  gtmDataLayerPush(event)
}
